// Importing necessary libraries and components
import * as React from 'react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { addMonths, subMonths, format, differenceInCalendarMonths, isEqual as isSameDate } from 'date-fns';
import styled, { css } from 'styled-components';
import { IAppointmentBookingInformation } from '@/store/types';
import beTwMerge from '@/utils/beTwMerge';
import { getFormattedDate } from '@/utils/utils';

// Styled components for the Calendar
const Frame = styled.div`
  width: 100%;
  height: fit-content;
  border: 1px solid #d9d9d9;
`;

const Header = styled.div`
  font-size: 18px;
  border-bottom: 1px solid #d9d9d9;
  max-height: 36px;
  font-weight: bold;
  margin-auto;
  padding: 8px 0px;
  display: flex;
  justify-content: space-between;
`;

const Button = styled.div<{ rightArrow?: boolean }>`
  cursor: pointer;
  margin-top: -2px;

  ${props =>
    props.rightArrow &&
    css`
      -moz-transform: scale(-1, 1);
      -webkit-transform: scale(-1, 1);
      -o-transform: scale(-1, 1);
      -ms-transform: scale(-1, 1);
      transform: scale(-1, 1);
    `}
`;

const Body = styled.div`
  --line-offset: 0px;
  --line-thickness: 1px;
  --line-color: #d9d9d9;

  width: 100%;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  border: none;
`;

const Day = styled.div<{
  $isToday?: boolean;
  $isSelected?: boolean;
  $isBeforeToday?: boolean;
  $isText?: boolean;
  $isEmptyBox?: boolean;
  $day?: number;
  $afterLastDay?: boolean;
  $beforeStartDay?: boolean;
  $isLoading?: boolean;
  $hasNoSetsterTimes?: boolean;
}>`
  height: 100%;
  aspect-ratio: 64/56.39;
  display: flex;
  color: #2c2c2c;
  font-size: 12px;
  font-weight: 500;
  position: relative;

  ${props =>
    props.$hasNoSetsterTimes &&
    css`
      color: #808080;
      background-color: #f9f9f9;
      cursor: default;

      &:hover {
        color: #808080 !important;
        background-color: #f9f9f9 !important;
        cursor: default;
      }
    `}

  ${props =>
    props.$isEmptyBox &&
    css`
      cursor: default;
      background-color: #f9f9f9;
    `}
  ${props =>
    (props.$isSelected || props.$isEmptyBox) &&
    css`
      border: none;
    `}
  align-items: center;
  justify-content: center;
  cursor: pointer;

  ${props =>
    props.$isBeforeToday &&
    css`
      color: #808080;
      background-color: #f9f9f9;
      cursor: default;
    `}

  ${props =>
    props.$afterLastDay &&
    css`
      &:hover {
        cursor: default;
      }
    `}
  ${props =>
    props.$beforeStartDay &&
    css`
      &:hover {
        cursor: default;
      }
    `}

  ${props =>
    props.$isSelected &&
    css`
      color: #fff;
      background-color: #0d6363;
    `}

  ${props =>
    !props.$isBeforeToday &&
    !props.$isSelected &&
    !props.$isEmptyBox &&
    css`
      &:hover {
        color: #fff;
        background-color: #0d6363;
      }
    `}

  ${props =>
    props.$isLoading &&
    css`
      color: #808080;
      background-color: #f9f9f9;
      cursor: default;
    `}

  @media (min-width: 1024px) {
    ${props =>
      props.$isText &&
      css`
        padding: 4px 16px;
      `}
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    background-color: var(--line-color);
    z-index: 1;
  }

  // Column Borders
  &:nth-child(1)::before,
  &:nth-child(8)::before,
  &:nth-child(15)::before,
  &:nth-child(22)::before,
  &:nth-child(29)::before,
  &:nth-child(36)::before {
    background-color: transparent;
  }

  // Row Borders
  &::after {
    inline-size: 100%;
    block-size: var(--line-thickness);
    inset-inline-start: 0;
    inset-block-start: calc(var(--line-offset) * -1);
  }

  // Column Borders
  ${props => {
    if (!props.$isEmptyBox || props.$afterLastDay) {
      return css`
        &::before {
          inline-size: var(--line-thickness);
          block-size: 100%;
          inset-inline-start: calc(var(--line-offset) * -1);
        }
      `;
    }
  }}
`;

// Main Calendar component
export function Calendar({
  info,
  setInfo,
  scrollToTime,
}: {
  info: IAppointmentBookingInformation;
  setInfo: (key: string, value: any) => void;
  scrollToTime: () => void;
}) {
  'use memo';

  // Constants for days, months, and weekdays
  const DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const DAYS_LEAP = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const DAYS_OF_THE_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  // Initialize state variables
  const today = useMemo(() => new Date(), []); // should be a useRef?
  const [date, setDate] = useState<Date>(info.selectedDate !== null ? new Date(info.selectedDate) : new Date());
  const [day, setDay] = useState(date?.getDate());
  const [month, setMonth] = useState(date?.getMonth());
  const [year, setYear] = useState(date?.getFullYear());
  const [startDay, setStartDay] = useState(getStartDayOfMonth(date));
  const [selectedDay, setSelectedDay] = useState(info.selectedDate || today);
  const [isLoading, setIsLoading] = useState(true);

  // Ref for checking if the component has been called
  const hasCalledRef = useRef(false);

  // Effect to update date and start day when date changes
  useEffect(() => {
    setDay(date?.getDate());
    setMonth(date?.getMonth());
    setYear(date?.getFullYear());
    setStartDay(getStartDayOfMonth(date));
  }, [date]);

  // Effect to set selected date if it's not already set
  useEffect(() => {
    if (!info.selectedDate) {
      setInfo('selectedDate', new Date(year, month, day));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect to handle availability and first available date
  useEffect(() => {
    const hasAvailability = Object.keys(info.setsterAvailabilityDateTimes).length > 0;

    if (hasAvailability) {
      setIsLoading(false);

      if (!hasCalledRef.current) {
        if (info.firstAvailableDate instanceof Date && info.selectedTimeId === null) {
          const differenceInMonths = differenceInCalendarMonths(info.firstAvailableDate, today);
          const newStartingMonth = addMonths(today, differenceInMonths);

          setSelectedDay(info.firstAvailableDate);
          setDay(info.firstAvailableDate.getDate());
          setMonth(info.firstAvailableDate.getMonth());
          setYear(info.firstAvailableDate.getFullYear());
          setStartDay(getStartDayOfMonth(info.firstAvailableDate));
          setDate(newStartingMonth);
          setInfo('selectedDate', info.firstAvailableDate);
          hasCalledRef.current = true;
        }
      }
    }
  }, [selectedDay, setInfo, info.setsterAvailabilityDateTimes, info.firstAvailableDate, info.selectedTimeId, today]);

  const redirectDate = (selectedDate: Date) => {
    const differenceInMonths = differenceInCalendarMonths(getFormattedDate(selectedDate), today);
    const redirectMonth = addMonths(today, differenceInMonths);
    setSelectedDay(new Date(selectedDate));
    setDate(redirectMonth);
  };

  useEffect(() => {
    if (info.selectedDate) {
      redirectDate(info.selectedDate);
    }
  }, [info.selectedDate]);

  // Function to get start day of the month
  function getStartDayOfMonth(passedDate: Date) {
    const startDate = new Date(passedDate?.getFullYear(), passedDate?.getMonth(), 1).getDay();
    return startDate === 0 ? 7 : startDate;
  }

  // Function to check if it's a leap year
  function isLeapYear(leapYear: number) {
    return (leapYear % 4 === 0 && leapYear % 100 !== 0) || leapYear % 400 === 0;
  }

  // Function to check if a day is before today
  function getIsBeforeToday(d: number, firstAvailableDate?: Date) {
    if (!(firstAvailableDate instanceof Date)) {
      return false;
    }
    if (year < firstAvailableDate?.getFullYear()) return true;
    if (year === firstAvailableDate?.getFullYear() && month < firstAvailableDate?.getMonth()) return true;
    if (
      year === firstAvailableDate?.getFullYear() &&
      month === firstAvailableDate?.getMonth() &&
      d < firstAvailableDate?.getDate()
    )
      return true;
  }

  // Determine the number of days in the month
  const days = isLeapYear(year) ? DAYS_LEAP : DAYS;
  let calendarDaysArrays = Array(days[month] + startDay).fill(null);
  const calendarDaysArraysLength = calendarDaysArrays.length;

  // If the start day is 7, fill the array with null
  if (startDay === 7) {
    calendarDaysArrays = Array(days[month]).fill(null);
  }

  // Calculate the number of days to add to the array
  const sumDays =
    // eslint-disable-next-line no-nested-ternary
    calendarDaysArraysLength > 28 && calendarDaysArraysLength < 35
      ? 35 - calendarDaysArraysLength
      : calendarDaysArraysLength > 35
        ? 42 - calendarDaysArraysLength
        : 0;

  calendarDaysArrays.push(...Array(sumDays).fill(null));

  // Check if the previous and next months are available
  const isPrevMonthNotAvailable = differenceInCalendarMonths(date, today) === 0;
  const isNextMonthNotAvailable = differenceInCalendarMonths(addMonths(date, 1), today) === 13;

  // Render the Calendar component
  return (
    <Frame>
      <Header>
        <Button
          onClick={() => {
            if (!isPrevMonthNotAvailable) {
              const prevMonth = subMonths(date, 1);
              setDate(prevMonth);
            }
          }}
          className={beTwMerge('', isPrevMonthNotAvailable && 'hover:tw-cursor-default')}
        >
          <i
            className={beTwMerge(
              'iconfont iconfont-left tw-text-sm tw-font-thin tw-pl-6',
              isPrevMonthNotAvailable && 'tw-hidden'
            )}
          />
          {isPrevMonthNotAvailable && <div className="tw-w-[38px]" />}
        </Button>
        <p
          className={beTwMerge(
            'tw-m-0 tw-font-[500] tw-flex tw-items-center tw-text-sm tw-text-center tw-text-be-dark-gray'
          )}
        >
          {MONTHS[month]} {year}
        </p>
        <Button
          rightArrow
          onClick={() => {
            if (!isNextMonthNotAvailable) {
              const nextMonth = addMonths(date, 1);
              setDate(nextMonth);
            }
          }}
          className={beTwMerge('', isNextMonthNotAvailable && 'hover:tw-cursor-default')}
        >
          <i
            className={beTwMerge(
              'iconfont iconfont-left tw-text-sm tw-font-thin tw-pl-6',
              isNextMonthNotAvailable && 'tw-hidden'
            )}
          />
          {isNextMonthNotAvailable && <div className="tw-w-[38px]" />}
        </Button>
      </Header>

      {/* Subheader */}
      <div className="tw-flex tw-w-full">
        {DAYS_OF_THE_WEEK.map(d => (
          <div
            key={d}
            className="tw-font-[500] tw-flex tw-justify-center tw-text-xs tw-w-[calc(100%/7)] tw-py-4 lg:tw-py-2 tw-text-be-dark-gray"
          >
            {d}
          </div>
        ))}
      </div>

      <Body>
        {calendarDaysArrays.map((_, index) => {
          'use memo';

          const calendarDay = startDay === 7 ? index + 1 : index - (startDay - 1);
          const calendarDayActualStringDate = format(new Date(year, month, calendarDay), 'yyyy-MM-dd');

          return (
            <Day
              key={index}
              $isBeforeToday={getIsBeforeToday(calendarDay, info.firstAvailableDate)}
              $isToday={calendarDay === today.getDate()}
              $isSelected={
                calendarDay === new Date(selectedDay)?.getDate() &&
                month === new Date(selectedDay)?.getMonth() &&
                year === new Date(selectedDay)?.getFullYear()
              }
              $isEmptyBox={calendarDay <= 0 || calendarDay > days[month]}
              $beforeStartDay={calendarDay < startDay}
              $afterLastDay={calendarDay > days[month]}
              $hasNoSetsterTimes={
                !info.setsterAvailabilityDateTimes[`${calendarDayActualStringDate}`] &&
                !info.setsterAvailabilityDateNightTimes[`${calendarDayActualStringDate}`]
              }
              $isLoading={isLoading}
              onClick={() => {
                const newDate = new Date(year, month, calendarDay);

                if (
                  !getIsBeforeToday(calendarDay, info.firstAvailableDate) &&
                  !isSameDate(newDate, new Date(selectedDay)) &&
                  !(calendarDay > days[month]) &&
                  (!!info.setsterAvailabilityDateTimes[`${calendarDayActualStringDate}`] ||
                    !!info.setsterAvailabilityDateNightTimes[`${calendarDayActualStringDate}`])
                ) {
                  setSelectedDay(newDate);
                  setInfo('selectedDate', newDate);

                  setInfo('selectedTime', '');
                  (window as any).dataLayer.push({
                    event: 'appointment_form',
                    showroom_page_section: 'select date and time',
                    showroom_action: 'calendar selection',
                    showroom_location: info.selectedShowroomStore.modalName,
                    general_event_details: newDate.toISOString().split('T')[0],
                  });
                  scrollToTime();
                }
              }}
            >
              {calendarDay > 0 && calendarDay <= days[month] ? calendarDay : ''}
            </Day>
          );
        })}
      </Body>
    </Frame>
  );
}
